import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { ChevronDown, ChevronUp, Users } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ClientsKeys,
  getReferredClients,
  ReferredClient,
} from "@/features/clients/api/clients";
import { formatCurrency } from "@/utils/currencies";

type SortKey = "name" | "customerEmail" | "founderName" | "createdAt";
type SortDirection = "asc" | "desc";

const sortReferredClients = (
  clients: ReferredClient[],
  sortKey: SortKey,
  sortDirection: SortDirection
): ReferredClient[] => {
  return [...clients].sort((a, b) => {
    let aValue: any;
    let bValue: any;

    if (sortKey === "founderName") {
      aValue = `${a.licenseeId.firstName} ${a.licenseeId.lastName}`;
      bValue = `${b.licenseeId.firstName} ${b.licenseeId.lastName}`;
    } else if (sortKey === "createdAt") {
      aValue = new Date(a.createdAt).getTime();
      bValue = new Date(b.createdAt).getTime();
    } else if (sortKey === "name") {
      aValue = a.name || "";
      bValue = b.name || "";
    } else {
      aValue = a[sortKey];
      bValue = b[sortKey];
    }

    if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
    if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });
};

const ReferralsTable = () => {
  const [sortKey, setSortKey] = useState<SortKey>("createdAt");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const { data: referredClients = [], isLoading } = useQuery({
    queryKey: [ClientsKeys.ReferredClients],
    queryFn: () => getReferredClients(),
  });

  const sortedClients = sortReferredClients(
    referredClients,
    sortKey,
    sortDirection
  );

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
  };

  const SortableHeader = ({
    sortKey: headerSortKey,
    children,
  }: {
    sortKey: SortKey;
    children: React.ReactNode;
  }) => (
    <TableHead
      className="px-3 py-1.5 text-[10px] font-semibold tracking-[-0.4px] text-[#767677] cursor-pointer select-none bg-transparent"
      onClick={() => handleSort(headerSortKey)}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortKey === headerSortKey &&
          (sortDirection === "asc" ? (
            <ChevronUp className="h-3 w-3" />
          ) : (
            <ChevronDown className="h-3 w-3" />
          ))}
      </div>
    </TableHead>
  );

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(4)].map((_, index) => (
          <div key={index} className="flex items-center gap-4">
            <Skeleton className="h-10 w-full" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
      {sortedClients.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16">
          <Users className="h-5 w-5 text-[#767677]" />
          <div className="mt-2 text-[#767677] text-sm font-semibold">
            No referrals yet
          </div>
        </div>
      ) : (
        <Table bordered={false}>
          <TableHeader className="bg-transparent">
            <TableRow>
              <SortableHeader sortKey="name">Lead Name</SortableHeader>
              <SortableHeader sortKey="customerEmail">
                Lead Email
              </SortableHeader>
              <SortableHeader sortKey="founderName">
                Assigned Founder
              </SortableHeader>
              <TableHead className="px-3 py-1.5 text-[10px] font-semibold tracking-[-0.4px] text-[#767677]">
                Notes
              </TableHead>
              <TableHead className="px-3 py-1.5 text-[10px] font-semibold tracking-[-0.4px] text-[#767677]">
                Status
              </TableHead>
              <TableHead className="px-3 py-1.5 text-[10px] font-semibold tracking-[-0.4px] text-[#767677]">
                Total Fees Earned
              </TableHead>
              <SortableHeader sortKey="createdAt">Date Referred</SortableHeader>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedClients.map((client) => (
              <TableRow
                key={client._id}
                className="hover:bg-sds-neutral-00 transition-colors"
              >
                <TableCell className="px-3 py-1.5 text-[12px] tracking-[-0.48px] text-[#494949] font-semibold">
                  {client.name || "-"}
                </TableCell>
                <TableCell className="px-3 py-1.5 text-[12px] tracking-[-0.48px] text-[#494949]">
                  {client.customerEmail}
                </TableCell>
                <TableCell className="px-3 py-1.5 text-[10px] tracking-[-0.4px] text-[#494949]">
                  {client.licenseeId.firstName} {client.licenseeId.lastName}
                </TableCell>
                <TableCell className="px-3 py-1.5 text-[10px] tracking-[-0.4px] text-[#494949] max-w-xs truncate">
                  {client.notes || "-"}
                </TableCell>
                <TableCell className="px-3 py-1.5">
                  <Badge
                    variant="opaque"
                    color={client.status === "paid" ? "green" : "blue"}
                    className="whitespace-nowrap px-[6px] py-[2px] text-[12px]"
                  >
                    {client.status === "paid" ? "Paid" : "Referred"}
                  </Badge>
                </TableCell>
                <TableCell className="px-3 py-1.5 text-[12px] tracking-[-0.48px] text-[#494949]">
                  {client.totalFeesCollected != null
                    ? formatCurrency(client.totalFeesCollected)
                    : "-"}
                </TableCell>
                <TableCell className="px-3 py-1.5 text-[10px] tracking-[-0.4px] text-[#494949]">
                  {format(new Date(client.createdAt), "MMM dd, yyyy")}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </>
  );
};

export default ReferralsTable;
